/**
 * The "turn on alerts" prompt. Granted hides it. Turning permission back off
 * starts the prompt again. Dismiss and trash are per-browser flags.
 */

import type { NoticeFilter } from "@shared/push-shared.ts";

export const EDM_PUSH_EVENT = "edm-push";
export const PUSH_OPTIN_NOTICE_ID = "edm-push-optin";

const TOAST_KEY = "edm-push-optin-toast-dismissed";
const TRASH_KEY = "edm-push-optin-inbox-trashed";
const READY_KEY = "edm-push-optin-was-ready";
const LAST_PERM_KEY = "edm-push-optin-last-permission";

export type PushOptInPermission = NotificationPermission | "unsupported";

function flagOn(key: string) {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean) {
  try {
    if (on) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    // ponytail: private mode still applies for this visit via caller state
  }
}

function readLastPermission(): NotificationPermission | null {
  try {
    const value = localStorage.getItem(LAST_PERM_KEY);
    if (value === "granted" || value === "denied" || value === "default") return value;
    return null;
  } catch {
    return null;
  }
}

function writeLastPermission(value: NotificationPermission) {
  try {
    localStorage.setItem(LAST_PERM_KEY, value);
  } catch {
    // ponytail: private mode still applies for this visit via caller state
  }
}

export function isPushOptInToastDismissed() {
  return flagOn(TOAST_KEY);
}

export function dismissPushOptInToast() {
  writeFlag(TOAST_KEY, true);
}

export function isPushOptInTrashed() {
  return flagOn(TRASH_KEY);
}

export function trashPushOptIn() {
  writeFlag(TRASH_KEY, true);
}

export function restorePushOptIn() {
  writeFlag(TRASH_KEY, false);
}

/** Granted hides the prompt. granted→off or first-seen denied resets dismiss/trash. */
export function applyPushOptInPermissionTransition(
  last: NotificationPermission | null,
  now: PushOptInPermission,
) {
  const hide = now === "granted";
  const reset = (last === "granted" && now !== "granted") || (last === null && now === "denied");
  return { hide, reset };
}

/** True→false means they turned push off; start the prompt from scratch. */
export function applyPushOptInReadyTransition(wasReady: boolean, ready: boolean) {
  if (ready) return { wasReady: true, reset: false };
  if (wasReady) return { wasReady: false, reset: true };
  return { wasReady: false, reset: false };
}

export function syncPushOptInVisibility() {
  const now: PushOptInPermission =
    typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  const last = readLastPermission();
  const leftoverReady = flagOn(READY_KEY);
  const fromReady = applyPushOptInReadyTransition(leftoverReady, now === "granted");
  const fromPerm = applyPushOptInPermissionTransition(last, now);
  const reset = fromPerm.reset || fromReady.reset;
  if (now !== "unsupported") writeLastPermission(now);
  writeFlag(READY_KEY, now === "granted");
  if (reset) {
    writeFlag(TOAST_KEY, false);
    writeFlag(TRASH_KEY, false);
  }
  return now === "granted";
}

export function watchPushOptInVisibility(onChange: () => void) {
  onChange();
  function onPush() {
    onChange();
  }
  function onVisible() {
    if (document.visibilityState === "visible") onChange();
  }
  window.addEventListener(EDM_PUSH_EVENT, onPush);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onPush);
  let status: PermissionStatus | undefined;
  if (navigator.permissions?.query) {
    void navigator.permissions
      .query({ name: "notifications" })
      .then((next) => {
        status = next;
        next.onchange = () => onChange();
      })
      .catch(() => {
        // ponytail: Safari without this query still has visibilitychange + focus
      });
  }
  return () => {
    window.removeEventListener(EDM_PUSH_EVENT, onPush);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", onPush);
    if (status) status.onchange = null;
  };
}

export function pushOptInCopy(input: { homeScreen: boolean }) {
  if (input.homeScreen) {
    return {
      title: "Add eConnect to your Home Screen first",
      body: "iPhone only sends campaign alerts from the Home Screen app. Tap Add to Home Screen, then open eConnect from that icon.",
    };
  }
  return {
    title: "Get alerts about your campaigns",
    body: "We’ll ping this device when artwork is approved or rejected. Tap Allow, then Allow again in the browser prompt.",
  };
}

/** All and search, or Trash. */
export function shouldShowPushOptInNotice(
  filter: NoticeFilter,
  trashed: boolean,
  q = "",
  title = "",
  body = "",
) {
  if (filter === "trash" ? !trashed : trashed) return false;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return title.toLowerCase().includes(needle) || body.toLowerCase().includes(needle);
}
